import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { from, Observable, switchMap, tap, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { TokenService } from '../services/auth/token.service';
import { AppErrorCode, BackEndErrorState } from '../services/error/error.constants';
import { NGXLogger } from 'ngx-logger';
import { AuthStatus } from '../services/user/user.type';
import { AuthService } from '../services/auth/auth.service';
import { IAuthenticatedUser } from '../services/auth/auth.type';
import { oAuthEndpointPath } from '../services/API/API.config';
import { ErrorService } from '../services/error/error.service';
import { APIHTTPErrorResponse } from '../models/API.models';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private tokenService: TokenService,
    private authService: AuthService,
    private logger: NGXLogger,
    private errorService: ErrorService
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    this.logger.debug('[Interceptor] request: ', request);

    return next.handle(request).pipe(
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse && event.status === 200) {
          this.logger.debug('[Interceptor] Intercepted 200 event: ', event);
        } else {
          this.logger.debug('[Interceptor] Intercepted Other event: ', event);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        this.logger.debug('[Interceptor] processing error : ', error);

        if (error.status === 400) {
          this.logger.debug('[Interceptor] Processing 400 error');

          // if 400 error comes from /oauth2/token path
          // we try to reAuthenticate and retry request
          if (error.url.includes(oAuthEndpointPath.token)) {
            this.logger.debug('[Interceptor] Retry currentAuthenticatedUser()');
            this.tokenService.currentAuthenticatedUser().then(async (user) => {
              if (!user) {
                this.logger.error(
                  '[Interceptor] Retry currentAuthenticatedUser() failed. Bailing out'
                );
                return this.errorService.routeToAuthError(AppErrorCode.AUTH_ERROR);
              }
              else {
                this.logger.debug('[Interceptor] Retrying initial the request');
                // Repeat request with new session
                return next.handle(this.retryRequest(request, user));
              }
            });
          }
          //else -> get out to the end of the catchError block
        } else if (error.status === 401) {
          this.logger.debug('[Interceptor] Processing 401 error');

          return from(this.authService.forceRefresh()).pipe(
            switchMap((status): Observable<HttpEvent<any>> | null => {
              if (AuthStatus.SUCCESS === status) {
                this.logger.debug(
                  '[Interceptor] forceRefresh() SUCCESS. Retrying the request'
                );

                // Repeat request with new session
                const user = this.tokenService.storedAuthenticatedUser();
                return next.handle(this.retryRequest(request, user));
              }
              else {
                // AuthStatus.ERROR, IDLE, IN_PROGRESS
                this.logger.error(
                  '[Interceptor] forceRefresh() failed. Bailing out'
                );
                return this.errorService.routeToAuthError(AppErrorCode.AUTH_REFRESH_FAILED);
              }
            })
          );
        } else if (
          error.status === 404
          || error.status >= 500
        ) {
          this.logger.warn('[Interceptor] Error 5XX', error.statusText);

          const publicErrorCode = error.error?.errorCode;
          this.logger.debug('[Interceptor] code: ', publicErrorCode);
          const beErrorState = this.errorService.backEndErrorStateFromAPIErrorCode(publicErrorCode);

          if (BackEndErrorState.GENERIC_AUTH_ERROR == beErrorState) {
            return this.errorService.routeToAuthError(
              AppErrorCode.AUTH_ERROR,
              publicErrorCode
            );
          }
          //else -> get out to the end of the catchError block
        }

        const apiError = new APIHTTPErrorResponse(error);
        this.errorService.logAPIErrorToSentry(apiError);
        return throwError(() => apiError);
      })
    );
  }

  retryRequest(
    request: HttpRequest<unknown>,
    user: IAuthenticatedUser
  ): HttpRequest<unknown> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${user.token}`,
      },
    });
  }
}
